import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

/** Anything a browser will put focus on. `[tabindex="-1"]` is excluded on
 *  purpose: the panel itself carries one, and it is focused directly rather
 *  than tabbed to. */
const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * A bottom sheet. Escape closes it, the scrim closes it, focus starts inside,
 * stays inside, and goes back where it came from.
 *
 * It renders into document.body rather than in place: the masthead carries a
 * backdrop-filter, which makes it a containing block for anything fixed inside
 * it, and a sheet rendered there is clipped to the masthead's 62px strip.
 *
 * **Three things the first version got wrong**, all found by keyboard rather
 * than by axe, which passes on every one of them (R2-12). Tab from the sheet's
 * last control walked into the page behind the scrim, while `aria-modal` told
 * a screen reader the background was not there; a keyboard user could operate
 * the three Buy buttons under a modal that claimed to be modal. Escape closed
 * the sheet and left focus wherever it had wandered to, which on the credits
 * screen was a money control the parent had not chosen. And nothing behind the
 * scrim was `inert`, so `aria-modal` was the only thing making the claim.
 *
 * So: every sibling of the scrim goes inert while the sheet is open, Tab and
 * Shift+Tab cycle inside the panel, and the element that opened the sheet is
 * remembered on the way in and focused again on the way out.
 *
 * **The effect depends on `open` and nothing else, deliberately.** Every caller
 * passes an inline arrow for `onClose`, so a dependency on it re-runs this on
 * every render, and a re-run would capture the panel itself as the element to
 * go back to. `onClose` is read through a ref instead.
 */
export function Sheet({ open, onClose, title, children }: SheetProps) {
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!open) return;

    /* Where focus came from. Read before the panel takes it. */
    const from = document.activeElement;
    opener.current = from instanceof HTMLElement ? from : null;

    /* Everything behind the scrim stops existing for the keyboard and for a
       screen reader. The scrim is the panel's parent, and it is the one child
       of the body that must stay live. */
    const scrim = panel.current?.parentElement ?? null;
    const hidden: HTMLElement[] = [];
    for (const node of Array.from(document.body.children)) {
      if (node === scrim || !(node instanceof HTMLElement)) continue;
      if (node.hasAttribute("inert")) continue;
      node.setAttribute("inert", "");
      hidden.push(node);
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close.current();
        return;
      }
      if (event.key !== "Tab") return;
      const box = panel.current;
      if (!box) return;
      const stops = Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (stops.length === 0) {
        // Nothing to move to, so the panel keeps focus rather than losing it.
        event.preventDefault();
        box.focus();
        return;
      }
      const first = stops[0]!;
      const last = stops[stops.length - 1]!;
      const here = document.activeElement;
      /* The panel itself counts as outside the ring. Measured in WebKit on 4
         September 2026: Tab from an element with `tabindex="-1"` does not
         continue into that element's own descendants, it hands focus to the
         document, so a sheet whose first Tab is not taken here loses the
         keyboard on press one. Chromium walks into the panel instead. Both
         end up on the same control because this decides it rather than the
         browser. */
      const adrift = here === null || here === box || !box.contains(here);
      if (event.shiftKey ? (adrift || here === first) : (adrift || here === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    };

    document.addEventListener("keydown", onKey);
    panel.current?.focus();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
      for (const node of hidden) node.removeAttribute("inert");
      /* Back where it came from. A control that has since left the page gets
         nothing rather than an error, and focus falls to the body. */
      const back = opener.current;
      opener.current = null;
      if (back && back.isConnected) back.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="sheet-scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet sheet-in" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={panel}>
        <div className="sheet-grip" aria-hidden="true" />
        {children}
      </div>
    </div>,
    document.body,
  );
}
